import { writesApi, deliveryApi } from './api';

// ── Types ─────────────────────────────────────────────────────────────────────

export type DeliveryProviderSlug = 'bolt' | 'glovo' | 'uber' | 'indriver' | 'demo';

export interface ThirdPartyDeliveryConfig {
  id: string;
  branchId: string;
  branchName?: string;
  businessName?: string;
  provider: DeliveryProviderSlug;
  apiMode: 'live' | 'sandbox';
  isActive: boolean;
  webhookConfigured: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProviderCredentialPayload {
  apiKey: string;
  apiSecret?: string;
  sandboxMode: boolean;
}

export interface ProviderPlatformConfig {
  provider: string;
  apiKeyMasked: string | null;
  hasApiSecret: boolean;
  sandboxMode: boolean;
  isConfigured: boolean;
}

export interface FleetSettings {
  acceptTimeoutMinutes: number;
  tier1WindowSeconds: number;
  tier2WindowSeconds: number;
  tier3WindowSeconds: number;
  maxConcurrentOrders: number;
}

export interface DeliveryProvidersSummary {
  bolt: number;
  glovo: number;
  uber: number;
  indriver: number;
}

export interface DeliveryProviderInfo {
  slug: DeliveryProviderSlug;
  displayName: string;
  uiColor: string;
  isDemoProvider: boolean;
  /** Platform-wide activation: false hides the provider from merchants and blocks dispatch */
  isActive: boolean;
  detailPath: string;
  isConfigured: boolean;
  sandboxMode: boolean;
  merchantCount: number;
}

export interface BroadcastStats {
  totalToday: number;
  openNow: number;
  takenToday: number;
  expiredToday: number;
}

export interface TripListingStats {
  openListings: number;
  claimedToday: number;
  convertedToBroadcast: number;
}

export interface DeliverySurcharge {
  id: string;
  surchargeType: string;
  /** KES flat fee: set when this is a flat surcharge; null otherwise */
  flatFee: number | null;
  /** Percentage of gross fee: set when this is a percentage surcharge; null otherwise */
  percentage: number | null;
  isActive: boolean;
  countryId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface VehicleRule {
  id: string;
  vehicleClass: string;
  displayName: string;
  maxWeightGrams: number;
  maxLengthMm: number;
  maxWidthMm: number;
  maxHeightMm: number;
  isEnclosed: boolean;
  isHazmatEligible: boolean;
  isNightEligible: boolean;
  supportsMultiStop: boolean;
  maxDistanceKm: number;
  displayOrder: number;
}

export interface DemoDispatchParams {
  simulatedAcceptanceRate: number;
  simulatedResponseTime: 'instant' | '30s' | '60s' | 'random';
  simulatedFailureRate: number;
}

export interface DemoDispatchResult {
  id: string;
  triggeredAt: string;
  outcome: 'accepted' | 'rejected' | 'timeout';
  responseTimeMs: number;
}

export interface WebhookEvent {
  id: string;
  provider: DeliveryProviderSlug;
  direction: 'inbound' | 'outbound';
  eventType: string;
  externalId: string | null;
  deliveryAssignmentId: string | null;
  status: 'pending' | 'processed' | 'failed';
  httpStatus: number | null;
  errorMessage: string | null;
  processedAt: string | null;
  createdAt: string;
}

export interface ApiCallLog {
  id: string;
  provider: DeliveryProviderSlug;
  callType: 'dispatch' | 'cancel' | 'track' | 'test' | 'quote';
  httpStatus: number | null;
  durationMs: number | null;
  success: boolean;
  errorMessage: string | null;
  deliveryAssignmentId: string | null;
  businessId: string | null;
  createdAt: string;
}


// ── Delivery incidents (disputed trips awaiting a support decision) ──────────

export interface DeliveryIncidentProof {
  id: string;
  kind: string;
  photoUrl: string;
  latitude: number | null;
  longitude: number | null;
  createdAt: string;
}

export interface DeliveryIncident {
  assignmentId: string;
  deliveryRef: string | null;
  orderId: string | null;
  orderNumber: number | null;
  status: string;
  isDisputed: boolean;
  createdAt: string;
  updatedAt: string;
  /** When the dispute was raised (first dispute event on the assignment) */
  raisedAt: string;
  rider: { id: string; name: string | null; phone: string | null } | null;
  /** The rider's own note, verbatim */
  riderNote: string | null;
  /** The dispute reason recorded on the event trail */
  reasonText: string | null;
  receivedBy: string | null;
  dropoff: {
    latitude: number | null;
    longitude: number | null;
    address: string | null;
    instructions: string | null;
  };
  riderLocation: {
    latitude: number;
    longitude: number;
    source: 'proof_photo' | 'report_event' | 'last_known_location' | null;
  } | null;
  /** Metres between the rider's recorded position and the drop-off, when both are known */
  distanceM: number | null;
  customer: { id: string; name: string | null; phone: string | null } | null;
  businessId: string | null;
  businessName: string | null;
  branchId: string | null;
  proofs: DeliveryIncidentProof[];
}

export interface DeliveryIncidentsResponse {
  incidents: DeliveryIncident[];
  total: number;
  limit: number;
  offset: number;
}

// ── Service ───────────────────────────────────────────────────────────────────

export const deliveryService = {
  //
  // Provider configs: reads via FastAPI would be better for paginated lists
  // but these admin endpoints live in Spring Boot (low-traffic admin reads).
  //
  listThirdPartyConfigs: (params?: { provider?: string; page?: number; limit?: number; search?: string }) =>
    writesApi.get<{ data: ThirdPartyDeliveryConfig[]; total: number; page: number; limit: number }>(
      '/admin/delivery/third-party-configs',
      { params },
    ),

  listProviders: () =>
    writesApi.get<DeliveryProviderInfo[]>('/admin/delivery/providers'),

  getProviderSummary: () =>
    writesApi.get<DeliveryProvidersSummary>('/admin/delivery/providers/summary'),

  getProviderConfig: (provider: DeliveryProviderSlug) =>
    writesApi.get<ProviderPlatformConfig>(`/admin/delivery/providers/${provider}/config`),

  updateProviderConfig: (provider: DeliveryProviderSlug, payload: ProviderCredentialPayload) =>
    writesApi.put<{ provider: string; isConfigured: boolean }>(
      `/admin/delivery/providers/${provider}/config`,
      payload,
    ),

  /** Activate/deactivate a provider platform-wide (merchant visibility + dispatch) */
  setProviderActivation: (provider: DeliveryProviderSlug, active: boolean) =>
    writesApi.put<{ slug: string; isActive: boolean }>(
      `/admin/delivery/providers/${provider}/activation`,
      { active },
    ),

  testProviderConnection: (provider: DeliveryProviderSlug) =>
    deliveryApi.post<{ success: boolean; latencyMs?: number; error?: string }>(
      `/admin/providers/${provider}/test`,
    ),

  //
  // Broadcast stats: Delivery Service :4000
  //
  getBroadcastStats: () =>
    deliveryApi.get<BroadcastStats>('/broadcasts/stats').catch(() => ({
      totalToday: 0,
      openNow: 0,
      takenToday: 0,
      expiredToday: 0,
    } as BroadcastStats)),

  getTripListingStats: () =>
    deliveryApi.get<TripListingStats>('/trips/stats').catch(() => ({
      openListings: 0,
      claimedToday: 0,
      convertedToBroadcast: 0,
    } as TripListingStats)),

  //
  // Surcharges: Spring Boot admin endpoints
  //
  listSurcharges: () =>
    writesApi.get<DeliverySurcharge[]>('/admin/delivery/surcharges'),

  updateSurcharge: (surchargeType: string, payload: { value: number; valueType: 'flat' | 'percentage' }) =>
    writesApi.put<DeliverySurcharge>(`/admin/delivery/surcharges/${surchargeType}`, payload),

  //
  // Vehicle rules: Spring Boot admin endpoints
  //
  listVehicleRules: () =>
    writesApi.get<VehicleRule[]>('/admin/delivery/vehicle-rules'),

  updateVehicleRule: (vehicleClass: string, payload: Partial<VehicleRule>) =>
    writesApi.put<VehicleRule>(`/admin/delivery/vehicle-rules/${vehicleClass}`, payload),

  //
  // Fleet settings: Spring Boot admin endpoints
  //
  getFleetSettings: () =>
    writesApi.get<FleetSettings>('/admin/delivery/fleet-settings'),

  updateFleetSettings: (payload: Partial<FleetSettings>) =>
    writesApi.put<FleetSettings>('/admin/delivery/fleet-settings', payload),

  //
  // Demo provider: Delivery Service :4000
  //
  triggerDemoDispatch: (params: DemoDispatchParams) =>
    deliveryApi.post<DemoDispatchResult>('/admin/demo/dispatch', params),

  listDemoDispatches: () =>
    deliveryApi.get<DemoDispatchResult[]>('/admin/demo/dispatches'),

  //
  // Webhook event log: Delivery Service :4000
  //
  listWebhookEvents: (provider: DeliveryProviderSlug, limit?: number) =>
    deliveryApi
      .get<{ success: boolean; data: WebhookEvent[] }>(`/admin/providers/${provider}/webhook-events`, {
        params: limit ? { limit } : undefined,
      })
      .then((r) => r.data),

  //
  // API call logs: Spring Boot reads from delivery_api_call_logs (written by Delivery Service)
  //
  listApiCallLogs: (provider: DeliveryProviderSlug, limit?: number) =>
    writesApi.get<ApiCallLog[]>('/admin/delivery/providers/' + provider + '/api-call-logs', {
      params: limit ? { limit } : undefined,
    }),


  //
  // Delivery incidents: Delivery Service :4000. Disputed trips (a rider reported
  // the customer was absent) sit here until support force-ends them deliberately.
  //
  listDeliveryIncidents: (params?: { status?: 'open' | 'all'; limit?: number; offset?: number }) =>
    deliveryApi.get<DeliveryIncidentsResponse>('/admin/delivery-incidents', params),

  getDeliveryIncidentCount: () =>
    deliveryApi
      .get<{ open: number }>('/admin/delivery-incidents/count')
      .catch(() => ({ open: 0 })),

  //
  // Force-end a stuck trip: Delivery Service :4000. Outcome decides the order's
  // fate; a reason is required; the rider's app clears the delivery immediately.
  //
  forceEndByAssignment: (assignmentId: string, outcome: ForceEndOutcome, reason: string) =>
    deliveryApi.post(`/admin/assignments/${assignmentId}/force-end`, { outcome, reason }),

  forceEndByOrder: (orderId: string, outcome: ForceEndOutcome, reason: string) =>
    deliveryApi.post(`/admin/orders/${orderId}/force-end`, { outcome, reason }),

  forceEndByRider: (riderId: string, outcome: ForceEndOutcome, reason: string) =>
    deliveryApi.post(`/admin/riders/${riderId}/force-end`, { outcome, reason }),
};

export type ForceEndOutcome = 'delivered' | 'redispatch' | 'cancel_refund' | 'free_rider';
